import React from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import SiteFooter from "@/components/SiteFooter";
import CalculatorTile from "@/components/CalculatorTile";
import ThemeToggle from "@/components/ThemeToggle";
import LangToggle from "@/components/LangToggle";
import { useLang } from "@/context/LangContext";
import { nzTaxLink } from "@/lib/conversion";
import {
  ArrowRight,
  Calculator,
  FileText,
  Car,
  Gauge,
  Percent,
  Split,
  Wallet,
  Hammer,
  ShieldCheck,
  Zap,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614585849038-272ee92d30fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjB0b29scyUyMGRhcmt8ZW58MHx8fHwxNzc4MDQzOTQ0fDA&ixlib=rb-4.1.0&q=85";

const WORKER_IMG =
  "https://images.unsplash.com/photo-1613326640775-f2e8f5dbf853?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxidWlsZGVyJTIwY29uc3RydWN0aW9uJTIwd29ya2VyfGVufDB8fHx8MTc3ODA0Mzk0NHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Calculator, key: "gst" },
  { icon: FileText, key: "quote" },
  { icon: Percent, key: "margin" },
  { icon: Split, key: "expense" },
  { icon: Wallet, key: "budget" },
  { icon: Car, key: "mileage" },
  { icon: Gauge, key: "tax" },
];

export default function Landing() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[var(--bg)] text-white">
      {/* NAV */}
      <header className="border-b border-[var(--border)] bg-[var(--bg)]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="landing-logo">
            <span className="sm:hidden">
              <Brand size="sm" variant="mark" />
            </span>
            <span className="hidden sm:flex">
              <Brand size="sm" />
            </span>
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2 shrink-0 min-w-0">
            <LangToggle />
            <ThemeToggle />
            <a
              href={nzTaxLink("/", "landing-nav-back")}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1 text-xs font-mono text-zinc-400 hover:text-[var(--primary)] mr-2"
              data-testid="nav-main-site"
            >
              nztaxation.nz <ExternalLink size={12} />
            </a>
            <span className="hidden md:inline-block">
              <Link
                to="/tools/gst"
                data-testid="nav-try-guest"
                className="btn-ghost text-xs"
              >
                {t("landing.cta_start", "Try as guest")}
              </Link>
            </span>
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden sm:inline-flex btn-ghost text-xs"
            >
              {t("landing.cta_login", "Login")}
            </Link>
            <Link to="/register" data-testid="nav-register" className="btn-primary text-xs whitespace-nowrap">
              <span className="sm:hidden">{t("landing.cta_register_short", "Sign up")}</span>
              <span className="hidden sm:inline">{t("landing.cta_register", "Get started")}</span>
              <ArrowRight size={14} />
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO + CALCULATORS (primary CTA — first thing on the page) */}
      <section className="relative overflow-hidden border-b border-[var(--border)]">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `var(--hero-overlay), url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-16">
          <div className="max-w-3xl stagger-in">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="chip chip-primary" data-testid="hero-chip">
                <Zap size={12} /> {t("landing.chip", "Free · No signup needed")}
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-7xl leading-[0.95] tracking-tight">
              {t("landing.hero_h1_line1", "Tradie Calculators")}
              <br />
              <span className="text-[var(--primary)]">{t("landing.hero_h1_line2", "for NZ.")}</span>
            </h1>
            <p className="mt-4 md:mt-6 text-base md:text-xl text-zinc-300 max-w-2xl">
              {t("landing.hero_sub_short", "Tap one to start — GST, quotes, mileage, tax, margin & markup. Built by IRD-registered tax agents at NZ Taxation Ltd.")}
            </p>
          </div>

          {/* THE 5 CALCULATORS — big, friendly, ready-to-tap */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mt-8 md:mt-10"
            data-testid="hero-calculator-grid"
          >
            <CalculatorTile
              to="/tools/gst"
              icon={Calculator}
              title={t("nav.gst", "GST")}
              sub={t("tile.gst.sub", "Add or remove 15%")}
              testId="hero-tile-gst"
              emphasised
            />
            <CalculatorTile
              to="/tools/quote"
              icon={FileText}
              title={t("nav.quote", "Quote / Invoice")}
              sub={t("tile.quote.sub", "Labour + materials")}
              testId="hero-tile-quote"
              emphasised
            />
            <CalculatorTile
              to="/tools/margin"
              icon={Percent}
              title={t("nav.margin", "Margin vs Markup")}
              sub={t("tile.margin.sub", "Don't mix them up")}
              testId="hero-tile-margin"
              emphasised
            />
            <CalculatorTile
              to="/tools/expense"
              icon={Split}
              title={t("nav.expense", "Business or Personal")}
              sub={t("tile.expense.sub", "What can you claim?")}
              testId="hero-tile-expense"
              emphasised
            />
            <CalculatorTile
              to="/tools/budget"
              icon={Wallet}
              title={t("nav.budget", "Budget")}
              sub={t("tile.budget.sub", "Plan every dollar")}
              testId="hero-tile-budget"
              emphasised
            />
            <CalculatorTile
              to="/tools/mileage"
              icon={Car}
              title={t("nav.mileage", "Mileage")}
              sub={t("tile.mileage.sub", "IRD rate × km")}
              testId="hero-tile-mileage"
              emphasised
            />
            <CalculatorTile
              to="/tools/tax"
              icon={Gauge}
              title={t("nav.tax", "Tax / PAYE")}
              sub={t("tile.tax.sub", "New brackets · 1 Apr 2025")}
              testId="hero-tile-tax"
              emphasised
            />
          </div>

          {/* SECONDARY: signup CTA + checklist */}
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-xs font-mono uppercase tracking-wider text-zinc-400">
              {t("landing.want_save", "Want to save your work?")}
            </span>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/register"
                data-testid="hero-cta-register"
                className="btn-primary text-xs"
              >
                {t("landing.cta_register", "Create free account")} <ArrowRight size={14} />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-login"
                className="btn-ghost text-xs"
              >
                {t("landing.cta_login", "Login")}
              </Link>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 md:gap-6 text-xs font-mono text-zinc-500">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[var(--primary)]" /> 15% GST baked in
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[var(--primary)]" /> New tax brackets · effective 1 Apr 2025
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[var(--primary)]" /> Invoice export
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES (descriptions of each calc — secondary, after the tappable tiles) */}
      <section className="border-b border-[var(--border)] grid-lines">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl">
            <div className="label-mono mb-3">/ 01 · {t("landing.what_each_eyebrow", "WHAT EACH ONE DOES").toUpperCase()}</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">
              {t("landing.pick_above", "Pick a calculator above.")}
            </h2>
            <p className="text-zinc-400 mt-3 text-sm md:text-base max-w-xl">
              {t("landing.not_sure_sub", "Not sure which one? Here's what each does — but the easiest is just to tap one.")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-0 mt-8 md:mt-12 border border-[var(--border)]">
            {features.map((f, i) => {
              const Icon = f.icon;
              const title = t(`feat.${f.key}.title`, f.key);
              const desc = t(`feat.${f.key}.desc`, "");
              return (
                <div
                  key={f.key}
                  data-testid={`feature-${i}`}
                  className="p-6 md:p-8 border-r border-b border-[var(--border)] last:border-r-0 hover:bg-[var(--surface)] transition-colors group"
                >
                  <Icon
                    size={28}
                    strokeWidth={1.5}
                    className="text-[var(--primary)] mb-4 group-hover:scale-110 transition-transform"
                  />
                  <div className="font-display text-lg md:text-xl mb-2">{title}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20 grid md:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="md:col-span-5">
            <div
              className="aspect-[4/5] bg-cover bg-center border border-[var(--border)]"
              style={{ backgroundImage: `url(${WORKER_IMG})` }}
              data-testid="who-image"
            />
          </div>
          <div className="md:col-span-7">
            <div className="label-mono mb-3">/ 02 · WHO IT'S FOR</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">
              Sparkies. Chippies.
              <br />
              Plumbers. Painters.
              <br />
              <span className="text-[var(--primary)]">Sole traders.</span>
            </h2>
            <p className="mt-6 text-zinc-300 max-w-lg">
              Whether you run a one-van operation in Hamilton or quote fitouts in
              Auckland, Tradie Calculator keeps your numbers tidy — built around
              NZ GST, IRD mileage rates and progressive PAYE.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="card-sharp p-4">
                <div className="label-mono">GST</div>
                <div className="font-mono text-2xl mt-1">15%</div>
              </div>
              <div className="card-sharp p-4">
                <div className="label-mono">IRD Mileage</div>
                <div className="font-mono text-2xl mt-1">$1.04/km</div>
              </div>
              <div className="card-sharp p-4">
                <div className="label-mono">Top PAYE</div>
                <div className="font-mono text-2xl mt-1">39%</div>
              </div>
              <div className="card-sharp p-4">
                <div className="label-mono">Brackets</div>
                <div className="font-mono text-2xl mt-1">5</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
          <ShieldCheck size={40} strokeWidth={1.5} className="mx-auto mb-6 text-[var(--primary)]" />
          <h2 className="font-display text-4xl md:text-6xl tracking-tight">
            Ready to tidy up your books?
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Free to start. No credit card. Your data stays on your account.
          </p>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/register" data-testid="cta-register" className="btn-primary">
              Start free <ArrowRight size={16} />
            </Link>
            <Link to="/tools/gst" data-testid="cta-guest" className="btn-ghost">
              Use as guest
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 text-center text-xs font-mono text-zinc-500 hidden">
        © {new Date().getFullYear()} NZ TAXATION LTD · Tradie Calculator · Made in Aotearoa
      </footer>
      <SiteFooter />
    </div>
  );
}
